import React, { useState, useEffect } from 'react';
import { ArrowLeft, Leaf, Users, DollarSign, TrendingUp, Award } from 'lucide-react';

/**
 * SeeYourImpact Page - Impact dashboard
 * Shows environmental, community, and cost savings impact based on user's reservation history
 */
function SeeYourImpact({ onBack }) {
  const [impactData, setImpactData] = useState({
    environmental: {
      co2Saved: 127, // kg
      wasteReduced: 45, // kg
      itemsReused: 23,
    },
    community: {
      organizationsHelped: 5,
      connectionsMade: 12,
      sharingScore: 87,
    },
    cost: {
      moneySaved: 3420, // dollars
      rentalValue: 5200,
      savingsPercent: 66,
    },
    totalReservations: 23,
  });

  // TODO: Fetch real data from Supabase based on user's reservation history
  useEffect(() => {
    console.log('Load impact data from Supabase');
  }, []);

  const ImpactCard = ({ icon: Icon, title, stats, color }) => (
    <div className={`bg-white rounded-lg border-2 ${color} p-6 hover:shadow-lg transition-shadow`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-12 h-12 ${color.replace('border', 'bg').replace('500', '100')} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color.replace('border', 'text')}`} />
        </div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      </div>
      <div className="space-y-3">
        {stats.map((stat, index) => (
          <div key={index}>
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm text-gray-600">{stat.label}</span>
              <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
            </div>
            {stat.subtitle && (
              <p className="text-xs text-gray-500">{stat.subtitle}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gray-50 z-[200]">
      <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 py-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back to Search</span>
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Your Impact</h1>
              <p className="text-sm text-gray-600">See the difference you're making with PropShare</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Stats */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg p-8 mb-8 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Impact Champion!</h2>
          </div>
          <p className="text-blue-100 mb-6 max-w-2xl">
            You've made {impactData.totalReservations} prop reservations, contributing to a more
            sustainable and connected theater community. Here's your impact breakdown:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="text-3xl font-bold mb-1">{impactData.environmental.co2Saved} kg</div>
              <div className="text-blue-100 text-sm">CO₂ Prevented</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="text-3xl font-bold mb-1">{impactData.community.organizationsHelped}</div>
              <div className="text-blue-100 text-sm">Organizations Helped</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="text-3xl font-bold mb-1">${impactData.cost.moneySaved.toLocaleString()}</div>
              <div className="text-blue-100 text-sm">Money Saved</div>
            </div>
          </div>
        </div>

        {/* Impact Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Environmental Impact */}
          <ImpactCard
            icon={Leaf}
            title="Environmental"
            color="border-green-500"
            stats={[
              {
                label: 'CO₂ Emissions Prevented',
                value: `${impactData.environmental.co2Saved} kg`,
                subtitle: 'Equivalent to planting 6 trees',
              },
              {
                label: 'Waste Reduced',
                value: `${impactData.environmental.wasteReduced} kg`,
                subtitle: 'Props reused instead of manufactured',
              },
              {
                label: 'Items Reused',
                value: impactData.environmental.itemsReused,
                subtitle: 'Props shared across productions',
              },
            ]}
          />

          {/* Community Impact */}
          <ImpactCard
            icon={Users}
            title="Community"
            color="border-blue-500"
            stats={[
              {
                label: 'Organizations Helped',
                value: impactData.community.organizationsHelped,
                subtitle: 'Theater companies supported',
              },
              {
                label: 'Connections Made',
                value: impactData.community.connectionsMade,
                subtitle: 'New partnerships formed',
              },
              {
                label: 'Sharing Score',
                value: `${impactData.community.sharingScore}%`,
                subtitle: 'Above average contributor',
              },
            ]}
          />

          {/* Cost Savings */}
          <ImpactCard
            icon={DollarSign}
            title="Cost Savings"
            color="border-yellow-500"
            stats={[
              {
                label: 'Total Savings',
                value: `$${impactData.cost.moneySaved.toLocaleString()}`,
                subtitle: 'By borrowing instead of buying',
              },
              {
                label: 'Estimated Rental Value',
                value: `$${impactData.cost.rentalValue.toLocaleString()}`,
                subtitle: 'Market value of props borrowed',
              },
              {
                label: 'Savings Percentage',
                value: `${impactData.cost.savingsPercent}%`,
                subtitle: 'Compared to purchasing new',
              },
            ]}
          />
        </div>

        {/* Encouragement Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <TrendingUp className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Keep Up the Great Work!</h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Every prop you borrow instead of buy reduces waste, saves money, and strengthens our
            theater community. Together, we're building a more sustainable future for the arts.
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}

export default SeeYourImpact;
